import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";

import {
  getPlatformAdminRoster,
  invitePlatformAdmin,
  revokePlatformAdmin,
  searchPlatformAdminCandidates,
  type PlatformAdminAccessCandidate,
  type PlatformAdminAccessState,
} from "@/shared/api/platformAdminAccess";
import {
  adminRosterAuthorityOutcome,
  normalizePlatformAdminEmail,
  PLATFORM_ADMIN_CANDIDATES_QUERY_KEY,
  PLATFORM_ADMIN_ROSTER_QUERY_KEY,
  refreshAdminRosterAuthority,
  refreshUserAdminAuthority,
  usePlatformAdminAccessAuthority,
  userAdminAuthorityOutcome,
  type PlatformAdminAccessRecovery,
  type PlatformAdminAccessWriteLease,
} from "@/shared/platformAdminAccessAuthority";
import { PLATFORM_ADMIN_ACCESS_MUTATION_KEY } from "@/shared/platformAdminAccessMutation";

const EMPTY_CANDIDATES: PlatformAdminAccessCandidate[] = [];

interface AdminInviteTarget {
  email: string;
  uid: string | null;
}

function describeAdminGrantError(error: unknown): string {
  if (error instanceof ApiError && error.message.includes("Google Workspace")) {
    return "Google Workspace directory access is not configured for this environment yet.";
  }
  return "Failed to grant platform admin access.";
}

function describeCandidateSearchError(error: unknown): string {
  if (
    error instanceof ApiError &&
    error.message.includes("Google Workspace directory search")
  ) {
    return "Google Workspace directory search is not configured for this environment yet.";
  }
  return "Failed to search the internal employee directory.";
}

function describeRevokeError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "SELF_REVOKE") {
      return "You can't revoke your own access.";
    }
    if (error.code === "LAST_PLATFORM_ADMIN") {
      return "Can't remove the last platform admin.";
    }
    if (error.status === 404) {
      return "No longer an admin.";
    }
  }
  return "Couldn't revoke access. Please try again.";
}

function candidateStateLabel(state: PlatformAdminAccessState): string {
  switch (state) {
    case "ready":
      return "Ready";
    case "already_admin":
      return "Admin";
    case "suspended":
      return "Blocked";
  }
}

function candidateStateColor(
  state: PlatformAdminAccessState,
): "default" | "error" | "success" {
  switch (state) {
    case "ready":
      return "success";
    case "already_admin":
      return "default";
    case "suspended":
      return "error";
  }
}

function candidateStateBody(candidate: PlatformAdminAccessCandidate): string {
  switch (candidate.access_state) {
    case "ready":
      return candidate.has_bw_identity
        ? "Already using BW. One fresh sign-in picks up access."
        : "Access activates on their first BW sign-in.";
    case "already_admin":
      return candidate.has_bw_identity
        ? "Already in the admins group."
        : "Already in the admins group. Access activates on first sign-in.";
    case "suspended":
      return "Suspended in Google Workspace.";
  }
}

interface RevokeControls {
  blocked: boolean;
  confirming: boolean;
  error: string | null;
  isOwnRow: boolean;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onStart: () => void;
}

function EmployeeCandidateRow({
  candidate,
  selected,
  onSelect,
  revoke,
}: {
  candidate: PlatformAdminAccessCandidate;
  selected: boolean;
  onSelect: () => void;
  revoke?: RevokeControls;
}) {
  const name = candidate.display_name || candidate.email;

  return (
    <Box sx={{ bgcolor: selected ? "action.selected" : "transparent", p: 1.5 }}>
      <ButtonBase
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        sx={{
          borderRadius: 1,
          display: "block",
          p: 1,
          textAlign: "left",
          width: "100%",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "flex-start", justifyContent: "space-between" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              title={name}
              variant="body2"
              sx={{ fontWeight: 700 }}
            >
              {name}
            </Typography>
            <Typography
              noWrap
              title={candidate.email}
              color="text.secondary"
              variant="caption"
            >
              {candidate.email}
            </Typography>
          </Box>
          <Chip
            color={candidateStateColor(candidate.access_state)}
            label={candidateStateLabel(candidate.access_state)}
            size="small"
            variant="outlined"
          />
        </Stack>
        {candidate.org_unit_path || candidate.has_bw_identity ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", mt: 1 }}>
            {candidate.org_unit_path ? (
              <Typography color="text.secondary" variant="caption">
                {candidate.org_unit_path}
              </Typography>
            ) : null}
            {candidate.has_bw_identity ? (
              <Typography color="text.secondary" variant="caption">
                BW identity
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </ButtonBase>

      {revoke ? (
        <Stack spacing={1} sx={{ px: 1, pt: 1 }}>
          {revoke.confirming ? (
            <>
              <Typography color="text.secondary" variant="caption">
                Remove platform admin access for {candidate.email}?
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  type="button"
                  size="small"
                  color="error"
                  variant="contained"
                  disabled={revoke.pending}
                  onClick={revoke.onConfirm}
                >
                  {revoke.pending ? "Revoking…" : "Yes, revoke access"}
                </Button>
                <Button
                  type="button"
                  size="small"
                  disabled={revoke.pending}
                  onClick={revoke.onCancel}
                >
                  Cancel
                </Button>
              </Stack>
            </>
          ) : (
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Button
                type="button"
                size="small"
                color="error"
                disabled={revoke.isOwnRow || revoke.blocked}
                aria-label={`Revoke admin access for ${candidate.email}`}
                title={
                  revoke.isOwnRow
                    ? "You can't revoke your own access"
                    : undefined
                }
                onClick={revoke.onStart}
              >
                Revoke
              </Button>
              {revoke.isOwnRow ? (
                <Typography color="text.secondary" variant="caption">
                  Your account
                </Typography>
              ) : null}
            </Stack>
          )}

          {revoke.error ? (
            <Alert severity="error" role="alert">
              {revoke.error}
            </Alert>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}

export function AdminAccessPanel() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const authority = usePlatformAdminAccessAuthority(queryClient);
  const privilegedWriteCount = useIsMutating({
    exact: true,
    mutationKey: PLATFORM_ADMIN_ACCESS_MUTATION_KEY,
  });
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [selectedCandidateEmail, setSelectedCandidateEmail] = useState<
    string | null
  >(null);
  const [confirmingRevokeEmail, setConfirmingRevokeEmail] = useState<
    string | null
  >(null);
  const [revokeError, setRevokeError] = useState<{
    email: string;
    message: string;
  } | null>(null);
  const [inviteResult, setInviteResult] = useState<{
    tone: "error";
    message: string;
  } | null>(null);
  const deferredEmployeeSearch = useDeferredValue(employeeSearch.trim());
  const initialResolutionSequence = useRef(authority.resolution?.sequence ?? 0);
  const currentEmail = normalizePlatformAdminEmail(user?.email);
  const authorityRecovery = authority.recovery;
  const authorityResolution =
    authority.resolution &&
    authority.resolution.sequence > initialResolutionSequence.current
      ? authority.resolution
      : null;
  const privilegedWriteBlocked =
    authority.recovery !== null ||
    authority.refreshing ||
    authority.writing ||
    privilegedWriteCount > 0;

  const rosterQuery = useQuery({
    queryKey: PLATFORM_ADMIN_ROSTER_QUERY_KEY,
    queryFn: () => getPlatformAdminRoster(80),
    retry: false,
  });

  const candidateQuery = useQuery({
    queryKey: [...PLATFORM_ADMIN_CANDIDATES_QUERY_KEY, deferredEmployeeSearch],
    queryFn: () => searchPlatformAdminCandidates(deferredEmployeeSearch, 8),
    enabled: deferredEmployeeSearch.length >= 2,
    retry: false,
  });

  const inviteMutation = useMutation({
    mutationKey: PLATFORM_ADMIN_ACCESS_MUTATION_KEY,
    mutationFn: async ({
      lease,
      target,
    }: {
      lease: PlatformAdminAccessWriteLease;
      target: AdminInviteTarget;
    }) => {
      const result = await invitePlatformAdmin(target.email);
      if (!authority.isWriteOperationCurrent(lease)) return null;
      const recovery = Object.freeze({
        email: target.email,
        kind: "invite",
        surface: "settings",
      }) satisfies PlatformAdminAccessRecovery;
      const responseMatches =
        normalizePlatformAdminEmail(result.email) ===
          normalizePlatformAdminEmail(target.email) &&
        (target.uid === null || result.uid === target.uid);
      const evidence = await refreshAdminRosterAuthority(
        queryClient,
        "reconcile",
        () => authority.isWriteOperationCurrent(lease),
      );
      if (!authority.isWriteOperationCurrent(lease)) return null;
      return { evidence, recovery, responseMatches };
    },
    retry: false,
  });

  const revokeMutation = useMutation({
    mutationKey: PLATFORM_ADMIN_ACCESS_MUTATION_KEY,
    mutationFn: async ({
      email,
      lease,
    }: {
      email: string;
      lease: PlatformAdminAccessWriteLease;
    }) => {
      await revokePlatformAdmin(email);
      if (!authority.isWriteOperationCurrent(lease)) return null;
      const recovery = Object.freeze({
        email,
        kind: "revoke",
        surface: "settings",
      }) satisfies PlatformAdminAccessRecovery;
      const evidence = await refreshAdminRosterAuthority(
        queryClient,
        "reconcile",
        () => authority.isWriteOperationCurrent(lease),
      );
      if (!authority.isWriteOperationCurrent(lease)) return null;
      return { evidence, recovery };
    },
    retry: false,
  });

  const isRosterView = deferredEmployeeSearch.length < 2;
  const displayedCandidates = isRosterView
    ? (rosterQuery.data?.items ?? EMPTY_CANDIDATES)
    : (candidateQuery.data?.items ?? EMPTY_CANDIDATES);

  useEffect(() => {
    if (displayedCandidates.length === 0) {
      setSelectedCandidateEmail(null);
      return;
    }
    if (
      !selectedCandidateEmail ||
      !displayedCandidates.some(
        (candidate) => candidate.email === selectedCandidateEmail,
      )
    ) {
      setSelectedCandidateEmail(displayedCandidates[0].email);
    }
  }, [displayedCandidates, selectedCandidateEmail]);

  const selectedCandidate = useMemo(
    () =>
      displayedCandidates.find(
        (candidate) => candidate.email === selectedCandidateEmail,
      ) ?? null,
    [displayedCandidates, selectedCandidateEmail],
  );

  function isOwnRow(email: string): boolean {
    return !currentEmail || normalizePlatformAdminEmail(email) === currentEmail;
  }

  function acquirePrivilegedWrite(): PlatformAdminAccessWriteLease | null {
    if (
      queryClient.isMutating({
        exact: true,
        mutationKey: PLATFORM_ADMIN_ACCESS_MUTATION_KEY,
      }) > 0 ||
      !authority.tryStartWrite()
    ) {
      return null;
    }
    const lease = authority.beginWriteOperation();
    if (!lease) authority.finishWrite();
    return lease;
  }

  async function handleInviteSubmit() {
    const candidate = selectedCandidate;
    const email = candidate?.email.trim();
    if (!candidate || !email || inviteMutation.isPending) {
      return;
    }
    const lease = acquirePrivilegedWrite();
    if (!lease) return;
    const target = Object.freeze({
      email,
      uid: candidate.firebase_uid ?? null,
    }) satisfies AdminInviteTarget;
    setInviteResult(null);
    try {
      const result = await inviteMutation.mutateAsync({ lease, target });
      if (!result || !authority.isWriteOperationCurrent(lease)) return;
      const { evidence, recovery, responseMatches } = result;
      if (!responseMatches) {
        authority.recordResolution(recovery, "identity-mismatch");
        return;
      }
      const outcome = adminRosterAuthorityOutcome(
        queryClient,
        recovery,
        evidence,
      );
      if (outcome === "uncertain") {
        authority.recordRecovery(recovery);
      } else {
        authority.recordResolution(recovery, outcome);
      }
    } catch (error) {
      if (!authority.isWriteOperationCurrent(lease)) return;
      setInviteResult({
        tone: "error",
        message: describeAdminGrantError(error),
      });
    } finally {
      authority.finishWrite(lease);
    }
  }

  function startRevoke(email: string) {
    if (isOwnRow(email) || privilegedWriteBlocked) {
      return;
    }
    setRevokeError(null);
    setConfirmingRevokeEmail(email);
  }

  function cancelRevoke() {
    if (privilegedWriteBlocked) return;
    setConfirmingRevokeEmail(null);
  }

  async function handleRevokeConfirm(email: string) {
    if (
      isOwnRow(email) ||
      confirmingRevokeEmail !== email ||
      revokeMutation.isPending
    ) {
      return;
    }
    const lease = acquirePrivilegedWrite();
    if (!lease) return;
    setRevokeError(null);
    try {
      const result = await revokeMutation.mutateAsync({ email, lease });
      if (!result || !authority.isWriteOperationCurrent(lease)) return;
      const { evidence, recovery } = result;
      setConfirmingRevokeEmail(null);
      const outcome = adminRosterAuthorityOutcome(
        queryClient,
        recovery,
        evidence,
      );
      if (outcome === "uncertain") {
        authority.recordRecovery(recovery);
      } else {
        authority.recordResolution(recovery, outcome);
      }
    } catch (error) {
      if (!authority.isWriteOperationCurrent(lease)) return;
      setConfirmingRevokeEmail(null);
      setRevokeError({ email, message: describeRevokeError(error) });
      if (error instanceof ApiError && error.status === 404) {
        await queryClient.invalidateQueries({
          exact: true,
          queryKey: PLATFORM_ADMIN_ROSTER_QUERY_KEY,
        });
      }
    } finally {
      authority.finishWrite(lease);
    }
  }

  async function retryAuthorityReads() {
    if (!authorityRecovery || privilegedWriteCount > 0) return;
    const recovery = authorityRecovery;
    if (!authority.tryStartRefresh(recovery)) return;
    const outcome =
      recovery.surface === "user"
        ? userAdminAuthorityOutcome(
            queryClient,
            recovery,
            await refreshUserAdminAuthority(
              queryClient,
              recovery.userId,
              "retry",
            ),
          )
        : adminRosterAuthorityOutcome(
            queryClient,
            recovery,
            await refreshAdminRosterAuthority(queryClient, "retry"),
          );
    if (outcome === "uncertain") {
      authority.finishRefresh(recovery);
      return;
    }
    authority.resolveRecovery(recovery, outcome);
  }

  let rosterBody;
  if (isRosterView && rosterQuery.isLoading) {
    rosterBody = (
      <Stack
        role="status"
        aria-live="polite"
        spacing={1}
        sx={{ alignItems: "center", py: 5 }}
      >
        <CircularProgress size={28} />
        <Typography color="text.secondary" variant="body2">
          Loading admins group…
        </Typography>
      </Stack>
    );
  } else if (!isRosterView && candidateQuery.isLoading) {
    rosterBody = (
      <Stack
        role="status"
        aria-live="polite"
        spacing={1}
        sx={{ alignItems: "center", py: 5 }}
      >
        <CircularProgress size={28} />
        <Typography color="text.secondary" variant="body2">
          Searching employees…
        </Typography>
      </Stack>
    );
  } else if (isRosterView && rosterQuery.isError) {
    rosterBody = (
      <Alert
        severity="error"
        action={
          authority.recovery ? undefined : (
            <Button
              color="inherit"
              size="small"
              disabled={rosterQuery.isFetching}
              onClick={() => void rosterQuery.refetch()}
            >
              Retry
            </Button>
          )
        }
      >
        Failed to load the admins group roster.
      </Alert>
    );
  } else if (!isRosterView && candidateQuery.isError) {
    rosterBody = (
      <Alert severity="error">
        {describeCandidateSearchError(candidateQuery.error)}
      </Alert>
    );
  } else if (displayedCandidates.length === 0) {
    rosterBody = (
      <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
        {isRosterView
          ? "No one is currently in the admins group."
          : "No internal employees matched that search."}
      </Typography>
    );
  } else {
    rosterBody = (
      <Stack divider={<Divider flexItem />}>
        {displayedCandidates.map((candidate) => (
          <EmployeeCandidateRow
            key={candidate.email}
            candidate={candidate}
            selected={candidate.email === selectedCandidateEmail}
            onSelect={() => {
              setSelectedCandidateEmail(candidate.email);
              setInviteResult(null);
            }}
            revoke={
              isRosterView
                ? {
                    isOwnRow: isOwnRow(candidate.email),
                    blocked: privilegedWriteBlocked,
                    confirming: confirmingRevokeEmail === candidate.email,
                    pending:
                      privilegedWriteCount > 0 &&
                      confirmingRevokeEmail === candidate.email,
                    error:
                      revokeError?.email === candidate.email
                        ? revokeError.message
                        : null,
                    onStart: () => startRevoke(candidate.email),
                    onCancel: cancelRevoke,
                    onConfirm: () => void handleRevokeConfirm(candidate.email),
                  }
                : undefined
            }
          />
        ))}
      </Stack>
    );
  }

  return (
    <Stack component="section" spacing={2}>
      <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
        Platform Admin Access
      </Typography>

      {authorityRecovery ? (
        <Alert
          severity="warning"
          role="alert"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={authority.refreshing}
              onClick={() => void retryAuthorityReads()}
            >
              {authority.refreshing ? "Retrying…" : "Retry"}
            </Button>
          }
        >
          {authorityRecovery.kind === "invite"
            ? `${authorityRecovery.email} needs verification. Admin access changed, but the current access lists could not be refreshed. Retry to confirm the latest access.`
            : `Admin access was revoked for ${authorityRecovery.email}, but the current access lists could not be refreshed. Retry to confirm the latest access.`}
        </Alert>
      ) : null}

      {authorityResolution?.outcome === "identity-mismatch" ? (
        <Alert severity="error" role="alert">
          The grant response did not match the selected employee.
        </Alert>
      ) : authorityResolution?.outcome === "diverged" ? (
        <Alert severity="error" role="alert">
          {authorityResolution.recovery.surface === "user"
            ? "The latest user access does not confirm this grant."
            : authorityResolution.recovery.kind === "invite"
              ? "The latest access does not match the requested grant."
              : "The latest access still shows this administrator."}
        </Alert>
      ) : null}

      {authorityResolution?.outcome === "confirmed" &&
      authorityResolution.recovery.kind === "invite" ? (
        <Alert severity="success" aria-live="polite">
          {authorityResolution.recovery.email} was added to
          admins@badmintonwarriors.com. If they are already signed in, they need
          one fresh platform sign-in.
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) 320px" },
        }}
      >
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Stack spacing={2} sx={{ p: 2 }}>
            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Typography
                component="h3"
                variant="subtitle1"
                sx={{ fontWeight: 700 }}
              >
                {isRosterView ? "Current admins" : "Employee search"}
              </Typography>
              {rosterQuery.data ? (
                <Chip
                  label={`${rosterQuery.data.total} admins`}
                  size="small"
                  variant="outlined"
                />
              ) : null}
            </Stack>
            <TextField
              type="search"
              label="Search employees"
              size="small"
              fullWidth
              placeholder="Search employees by name or email"
              value={employeeSearch}
              onChange={(event) => {
                setEmployeeSearch(event.target.value);
                setInviteResult(null);
              }}
            />
            <Typography color="text.secondary" variant="body2">
              Access comes from admins@badmintonwarriors.com.
            </Typography>
          </Stack>
          <Divider />
          <Box sx={{ minHeight: 180 }}>{rosterBody}</Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography
              component="h3"
              variant="subtitle1"
              sx={{ fontWeight: 700 }}
            >
              Selected employee
            </Typography>
            {selectedCandidate ? (
              <>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    noWrap
                    title={
                      selectedCandidate.display_name || selectedCandidate.email
                    }
                    sx={{ fontWeight: 700 }}
                  >
                    {selectedCandidate.display_name || selectedCandidate.email}
                  </Typography>
                  <Typography
                    noWrap
                    title={selectedCandidate.email}
                    color="text.secondary"
                    variant="body2"
                  >
                    {selectedCandidate.email}
                  </Typography>
                </Box>
                <Chip
                  label={candidateStateLabel(selectedCandidate.access_state)}
                  color={candidateStateColor(selectedCandidate.access_state)}
                  size="small"
                  variant="outlined"
                  sx={{ alignSelf: "flex-start" }}
                />
                {selectedCandidate.org_unit_path ? (
                  <Typography color="text.secondary" variant="body2">
                    {selectedCandidate.org_unit_path}
                  </Typography>
                ) : null}
                <Typography color="text.secondary" variant="body2">
                  {candidateStateBody(selectedCandidate)}
                </Typography>

                {inviteResult ? (
                  <Alert severity="error" aria-live="polite">
                    {inviteResult.message}
                  </Alert>
                ) : null}

                {selectedCandidate.access_state === "ready" ? (
                  <Button
                    type="button"
                    size="small"
                    variant="contained"
                    fullWidth
                    disabled={privilegedWriteBlocked}
                    onClick={() => void handleInviteSubmit()}
                  >
                    {inviteMutation.isPending
                      ? "Adding…"
                      : "Add to admins group"}
                  </Button>
                ) : (
                  <Typography color="text.secondary" variant="body2">
                    {selectedCandidate.access_state === "already_admin"
                      ? "No action needed."
                      : "This person cannot receive access."}
                  </Typography>
                )}
              </>
            ) : (
              <Typography color="text.secondary" variant="body2">
                Select a person from the roster or search results.
              </Typography>
            )}
          </Stack>
        </Paper>
      </Box>
    </Stack>
  );
}
